import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import type { TranscriptEntry, SessionToolCall } from '@/lib/api/sessions';
import ToolCallBubble from '@/components/sessions/ToolCallBubble';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface SessionTranscriptTimelineProps {
  transcripts: TranscriptEntry[];
  toolCalls: SessionToolCall[];
  className?: string;
  /** Current audio playback time in seconds (drives auto-scroll + highlight). */
  audioCurrentTime?: number;
  /** ISO timestamp of the session start — used to compute per-entry offsets. */
  sessionStartTime?: string;
  /** Called when a message is clicked — receives the time offset in seconds. */
  onSeek?: (timeInSeconds: number) => void;
  /** When true, tool call bubbles hide their inline media (media renders in ContentViewer instead). */
  hideToolCallMedia?: boolean;
}

type TimelineItem =
  | { kind: 'message'; data: TranscriptEntry }
  | { kind: 'tool_call'; data: SessionToolCall };

type TranscriptFilter = 'all' | 'person' | 'agent';

const isPersonSpeaker = (speaker: string) => speaker === 'user' || speaker === 'customer';
const isAgentItem = (item: TimelineItem) =>
  item.kind === 'tool_call' || (item.kind === 'message' && !isPersonSpeaker(item.data.speaker));

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  show_presentation_slide: 'Showed slide',
  fetch_video_segment: 'Showed video',
  replay_video: 'Replayed video',
  show_documentation: 'Showed documentation',
  schedule_meeting: 'Opened calendar',
};

/** Return the ISO timestamp string for a timeline item. */
const getItemTimestamp = (item: TimelineItem): string | null =>
  item.kind === 'message' ? item.data.timestamp : item.data.created_at;

const formatOffset = (seconds: number | null) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getSpeakerLabel = (speaker: string) => (isPersonSpeaker(speaker) ? 'Person' : 'Agent');

const getToolDisplayName = (toolCall: SessionToolCall) =>
  TOOL_DISPLAY_NAMES[toolCall.tool_name] || toolCall.tool_name.replace(/_/g, ' ');

const getOutputLabel = (outputs: Record<string, any> | null | undefined): string | null => {
  if (!outputs) return null;

  const direct = [
    outputs.title,
    outputs.custom_label,
    outputs.customLabel,
    outputs.video_title,
    outputs.videoTitle,
    outputs.video_name,
    outputs.videoName,
    outputs.segment_title,
    outputs.segmentTitle,
    outputs.asset_title,
    outputs.assetTitle,
    outputs.filename,
    outputs.name,
  ].find((value) => typeof value === 'string' && value.trim());

  if (direct) return String(direct);

  const firstSegment = Array.isArray(outputs.segments) ? outputs.segments[0] : null;
  if (firstSegment) {
    return getOutputLabel(firstSegment);
  }

  const matchedSegment = outputs.matched_segment || outputs.matchedSegment || outputs.segment || outputs.metadata;
  if (matchedSegment) {
    return getOutputLabel(matchedSegment);
  }

  return null;
};

const getToolDetail = (toolCall: SessionToolCall) => {
  const label = getOutputLabel(toolCall.outputs_json);
  if (label) return label;
  return toolCall.outputs_json?.description || null;
};

const getItemSearchText = (item: TimelineItem) => {
  if (item.kind === 'message') {
    return `${item.data.speaker} ${item.data.text}`;
  }

  return [
    item.data.tool_name,
    getToolDisplayName(item.data),
    getToolDetail(item.data),
    item.data.status,
  ].filter(Boolean).join(' ');
};

const highlightText = (text: string, query: string) => {
  const trimmed = query.trim();
  if (!trimmed) return text;

  const lower = text.toLowerCase();
  const needle = trimmed.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let matchIndex = lower.indexOf(needle);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(text.slice(cursor, matchIndex));
    }
    parts.push(
      <mark key={`${matchIndex}-${needle}`} className="rounded bg-amber-100 px-0.5 text-foreground">
        {text.slice(matchIndex, matchIndex + needle.length)}
      </mark>,
    );
    cursor = matchIndex + needle.length;
    matchIndex = lower.indexOf(needle, cursor);
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
};

const SessionTranscriptTimeline: React.FC<SessionTranscriptTimelineProps> = ({
  transcripts,
  toolCalls,
  className,
  audioCurrentTime,
  sessionStartTime,
  onSeek,
  hideToolCallMedia,
}) => {
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const lastScrolledIndex = useRef<number>(-1);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<TranscriptFilter>('all');
  const [syncToAudio, setSyncToAudio] = useState(true);

  const mergedTimeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    for (const msg of transcripts) {
      items.push({ kind: 'message', data: msg });
    }

    for (const tc of toolCalls) {
      items.push({ kind: 'tool_call', data: tc });
    }

    items.sort((a, b) => {
      const tsA = getItemTimestamp(a);
      const tsB = getItemTimestamp(b);
      if (!tsA && !tsB) return 0;
      if (!tsA) return 1;
      if (!tsB) return -1;
      return new Date(tsA).getTime() - new Date(tsB).getTime();
    });

    return items;
  }, [transcripts, toolCalls]);

  // Compute per-item offset (seconds from session start)
  const itemOffsets = useMemo(() => {
    if (!sessionStartTime) return null;
    const startMs = new Date(sessionStartTime).getTime();
    if (Number.isNaN(startMs)) return null;

    return mergedTimeline.map((item) => {
      const ts = getItemTimestamp(item);
      if (!ts) return null;
      const ms = new Date(ts).getTime();
      if (Number.isNaN(ms)) return null;
      return (ms - startMs) / 1000;
    });
  }, [mergedTimeline, sessionStartTime]);

  // Find which item is currently "active" based on audio playback time
  const activeIndex = useMemo(() => {
    if (!syncToAudio || audioCurrentTime == null || !itemOffsets) return -1;
    let best = -1;
    for (let i = 0; i < itemOffsets.length; i++) {
      const offset = itemOffsets[i];
      if (offset != null && offset <= audioCurrentTime) {
        best = i;
      }
    }
    return best;
  }, [audioCurrentTime, itemOffsets, syncToAudio]);

  // Auto-scroll to the active item
  useEffect(() => {
    if (!syncToAudio || activeIndex < 0 || activeIndex === lastScrolledIndex.current) return;
    lastScrolledIndex.current = activeIndex;
    const el = itemRefs.current.get(activeIndex);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeIndex, syncToAudio]);

  const setItemRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(index, el);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  const handleItemClick = useCallback((index: number) => {
    if (!onSeek || !itemOffsets) return;
    const offset = itemOffsets[index];
    if (offset != null) {
      onSeek(offset);
    }
  }, [onSeek, itemOffsets]);

  if (mergedTimeline.length === 0) return null;

  const visibleItems = mergedTimeline
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => {
      if (filter === 'person') {
        return item.kind === 'message' && isPersonSpeaker(item.data.speaker);
      }
      if (filter === 'agent') {
        return isAgentItem(item);
      }
      return true;
    })
    .filter(({ item }) => {
      const trimmedQuery = query.trim().toLowerCase();
      if (!trimmedQuery) return true;
      return getItemSearchText(item).toLowerCase().includes(trimmedQuery);
    });

  const syncEnabled = syncToAudio && audioCurrentTime != null && itemOffsets != null;
  const seekable = !!onSeek && itemOffsets != null;
  const filterOptions: { value: TranscriptFilter; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: mergedTimeline.length },
    { value: 'person', label: 'Person', count: mergedTimeline.filter((item) => item.kind === 'message' && isPersonSpeaker(item.data.speaker)).length },
    { value: 'agent', label: 'Agent', count: mergedTimeline.filter(isAgentItem).length },
  ];

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="sticky top-0 z-10 space-y-2 border-b border-border bg-background/95 px-3 py-2 backdrop-blur">
        <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search transcript"
              className="h-8 rounded-md pl-8 text-xs"
            />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center rounded-md border border-border bg-muted/30 p-0.5">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFilter(option.value)}
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded px-2 text-xs font-medium transition-colors',
                  filter === option.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
                <span className="text-[10px] text-muted-foreground">{option.count}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setSyncToAudio((value) => !value)}
            disabled={!itemOffsets}
            className={cn(
              'inline-flex h-8 items-center rounded-md border px-2 text-xs font-medium transition-colors',
              syncToAudio
                ? 'border-primary/30 bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
              !itemOffsets && 'cursor-not-allowed opacity-50',
            )}
          >
            Sync
          </button>
        </div>
      </div>

      <div className="relative flex-1 space-y-1 overflow-y-auto p-2">
        {visibleItems.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
            No transcript entries match this view.
          </div>
        ) : null}

        {visibleItems.map(({ item, index }) => {
        const isActive = syncEnabled && index === activeIndex;
        const offset = itemOffsets?.[index] ?? null;
        const canSeekToItem = seekable && offset != null;

        if (item.kind === 'tool_call') {
          const title = getToolDisplayName(item.data);
          const detail = getToolDetail(item.data);
          const primaryTitle = detail || title;
          return (
            <div
              key={`tc-${item.data.id}`}
              ref={(el) => setItemRef(index, el)}
              className={cn(
                'grid items-start rounded-md border border-transparent transition-colors duration-200',
                'grid-cols-[42px_58px_minmax(0,1fr)] gap-2 px-2 py-1',
                isActive && 'border-primary/30 bg-primary/5 shadow-[inset_2px_0_0_hsl(var(--primary))]',
                canSeekToItem && 'cursor-pointer hover:bg-muted/40',
              )}
              onClick={canSeekToItem ? () => handleItemClick(index) : undefined}
            >
              <span className={cn('pt-0.5 text-right font-mono text-[11px]', isActive ? 'text-primary' : 'text-muted-foreground')}>
                {formatOffset(offset)}
              </span>
              <span
                className="inline-flex items-center gap-1 pt-0.5 text-[11px] font-medium text-muted-foreground"
              >
                Agent
              </span>
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium text-foreground">
                    {highlightText(String(primaryTitle), query)}
                  </span>
                  {item.data.status ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
                      {item.data.status}
                    </span>
                  ) : null}
                </div>
                {detail ? (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {highlightText(title, query)}
                  </p>
                ) : null}
                {!hideToolCallMedia ? (
                  <div className="mt-2">
                    <ToolCallBubble toolCall={item.data} isActive={isActive} hideMedia={hideToolCallMedia} />
                  </div>
                ) : null}
              </div>
            </div>
          );
        }
        const message = item.data;
        return (
          <div
            key={`msg-${message.id || index}`}
            ref={(el) => setItemRef(index, el)}
            className={cn(
              'grid items-start rounded-md border border-transparent transition-colors duration-200',
              'grid-cols-[42px_58px_minmax(0,1fr)] gap-2 px-2 py-1',
              isActive && 'border-primary/30 bg-primary/5 shadow-[inset_2px_0_0_hsl(var(--primary))]',
              canSeekToItem && 'cursor-pointer hover:bg-muted/40',
            )}
            onClick={canSeekToItem ? () => handleItemClick(index) : undefined}
          >
            <span className={cn('pt-0.5 text-right font-mono text-[11px]', isActive ? 'text-primary' : 'text-muted-foreground')}>
              {formatOffset(offset)}
            </span>
            <span
              className="inline-flex items-center pt-0.5 text-[11px] font-medium text-muted-foreground"
            >
              {getSpeakerLabel(message.speaker)}
            </span>
            <div className="min-w-0">
              <p className="whitespace-pre-wrap text-[13px] leading-5 text-foreground">
                {highlightText(message.text, query)}
              </p>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default SessionTranscriptTimeline;
